import { Currency } from './Currency';

/**
 * @deprecated 계산에 오류가 있는 것 같습니다.
 */
export class Money {
  private readonly amount: number;
  private readonly currency: Currency;

  constructor(amount: number, currency: Currency = Currency.KRW) {
    this.amount = amount;
    this.currency = currency;
    this.validate();
  }

  static of(amount: number, currency: Currency = Currency.KRW): Money {
    return new Money(amount, currency);
  }

  static zero(currency: Currency = Currency.KRW): Money {
    return new Money(0, currency);
  }

  static won(amount: number): Money {
    return new Money(amount, Currency.KRW);
  }

  static dollar(amount: number): Money {
    return new Money(amount, Currency.USD);
  }

  static yen(amount: number): Money {
    return new Money(amount, Currency.JPY);
  }

  convertTo(targetCurrency: Currency): Money {
    if (this.currency.equals(targetCurrency)) {
      return this;
    }
    const exchangeRate = Currency.getExchangeRate(targetCurrency, this.currency);
    const convertedAmount = this.amount * exchangeRate;
    return new Money(convertedAmount, targetCurrency);
  }

  toKRW(): Money {
    return this.convertTo(Currency.KRW);
  }

  toUSD(): Money {
    return this.convertTo(Currency.USD);
  }

  toJPY(): Money {
    return this.convertTo(Currency.JPY);
  }

  add(other: Money): Money {
    return new Money(this.amount + other.amount, this.currency);
  }

  subtract(other: Money): Money {
    return new Money(this.amount - other.amount, this.currency);
  }

  multiply(multiplier: number | string): Money {
    const factor = typeof multiplier === 'string' ? parseFloat(multiplier) : multiplier;
    return new Money(this.amount * factor, this.currency);
  }

  divide(divisor: number | string): Money {
    const factor = typeof divisor === 'string' ? parseFloat(divisor) : divisor;
    if (factor === 0) {
      throw new Error('Division by zero');
    }
    return new Money(this.amount / factor, this.currency);
  }

  round(decimalPlaces?: number): Money {
    const places = decimalPlaces ?? this.currency.getDecimal();
    const factor = Math.pow(10, places);
    const roundedAmount = Math.round(this.amount * factor) / factor;
    return new Money(roundedAmount, this.currency);
  }

  equals(other: Money): boolean {
    return this.currency.equals(other.currency) && Math.abs(this.amount - other.amount) < Number.EPSILON;
  }

  isGreaterThan(other: Money): boolean {
    return this.amount > other.amount;
  }

  isLessThan(other: Money): boolean {
    return this.amount < other.amount;
  }

  isZero(): boolean {
    return Math.abs(this.amount) < Number.EPSILON;
  }

  isPositive(): boolean {
    return this.amount > 0;
  }

  isNegative(): boolean {
    return this.amount < 0;
  }

  negate(): Money {
    return new Money(-this.amount, this.currency);
  }

  abs(): Money {
    return new Money(Math.abs(this.amount), this.currency);
  }

  getAmount(): number {
    return this.amount;
  }

  getCurrency(): Currency {
    return this.currency;
  }

  private validate(): void {
    if (!isFinite(this.amount)) {
      throw new Error('Amount must be finite');
    }
    if (isNaN(this.amount)) {
      throw new Error('Amount must be a number');
    }
  }
}
